import { useEffect, useState } from "react";
import { api, downloadAuthed, printAuthedPdf, fetchPdfBlobUrl } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import FailureReasonsPicker from "@/components/admin/FailureReasonsPicker";
import ServiceNoteField from "@/components/admin/ServiceNoteField";
import InvoicePreview from "@/components/admin/InvoicePreview";
import ReceiptPrinter from "@/components/admin/ReceiptPrinter";
import { FileText, Printer, Pencil, X, Loader2 } from "lucide-react";

const Toggle = ({ value, options, onChange, testId }) => (
    <div className="inline-flex border-2 border-slate-900 overflow-hidden shrink-0" data-testid={testId}>
        {options.map(([v, label], i) => (
            <button
                key={v}
                type="button"
                onClick={() => onChange(v)}
                className={`px-3 py-1.5 text-xs font-display font-black uppercase tracking-wider transition-colors ${i ? "border-l-2 border-slate-900" : ""} ${value === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
                data-testid={`${testId}-${v}`}
            >
                {label}
            </button>
        ))}
    </div>
);

export const formatPrintLog = (lp) =>
    lp ? `${lp.doc_type === "preventiv" ? "Preventiv" : "Faturë"} · ${lp.format === "80mm" ? "80mm" : "A4"} · ${lp.currency} · ${new Date(lp.at).toLocaleString()}` : "";

export default function SaleInvoiceModal({ sale: initialSale, onClose, onEdit, onChanged }) {
    const { content } = useSiteContent();
    const last = initialSale.last_print || {};
    const [sale, setSale] = useState(initialSale);
    const [docType, setDocType] = useState(last.doc_type || "preventiv");
    const [currency, setCurrency] = useState(last.currency || "LEK");
    const [format, setFormat] = useState(last.format || "a4");
    const [view, setView] = useState(window.innerWidth < 768 ? "html" : "pdf");
    const [pdfUrl, setPdfUrl] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [receiptOpen, setReceiptOpen] = useState(false);
    const [serviceNote, setServiceNote] = useState(initialSale.service_note || "");
    const [savedNote, setSavedNote] = useState(initialSale.service_note || "");

    // Exact PDF (same file that gets printed) for the chosen options
    useEffect(() => {
        if (view !== "pdf") return;
        let url = null;
        let cancelled = false;
        setPdfUrl(null);
        const req = format === "80mm"
            ? fetchPdfBlobUrl("/admin/receipts/render-pdf", { body: { sale_id: sale.id, doc_type: docType, currency } })
            : fetchPdfBlobUrl(`/admin/sales/${sale.id}/pdf`, { params: { type: docType, currency, inline: true } });
        req.then((u) => { url = u; if (!cancelled) setPdfUrl(u); }).catch(() => {});
        return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    }, [view, format, docType, currency, sale.id, sale.failure_reasons, sale.services, sale.total, savedNote]);

    const logPrint = async (method, fmt = format) => {
        try {
            const { data } = await api.post(`/admin/sales/${sale.id}/print-log`, { doc_type: docType, currency, format: fmt, method });
            setSale((s) => ({ ...s, last_print: data.last_print, print_history: data.print_history }));
            onChanged?.();
        } catch {}
    };
    const saveExtras = async (patch) => {
        setSale((s) => ({ ...s, ...patch }));
        try {
            const { data } = await api.put(`/admin/sales/${sale.id}/failure-reasons`, patch);
            setSale((s) => ({ ...s, ...data, service_note: s.service_note }));
            onChanged?.();
        } catch (e) {
            alert(e.response?.data?.detail || "Ruajtja dështoi");
        }
    };
    const saveServiceNote = () => {
        if (serviceNote === savedNote) return;
        setSavedNote(serviceNote);
        saveExtras({ service_note: serviceNote });
    };
    const printA4 = async () => {
        setFormat("a4");
        setPrinting(true);
        try {
            await printAuthedPdf(`/admin/sales/${sale.id}/pdf`, { type: docType, currency, inline: true });
            logPrint("print", "a4");
        } catch (e) {
            alert(e.message);
        } finally {
            setPrinting(false);
        }
    };
    const print80 = () => { setFormat("80mm"); setReceiptOpen(true); };

    return (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div className="bg-white max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="sale-invoice-modal">
                <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap justify-between items-center gap-2">
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <select value={docType} onChange={(e) => setDocType(e.target.value)} className="input-field !py-1 !text-xs !w-auto shrink-0" data-testid="invoice-type-select">
                            <option value="invoice">Faturë</option>
                            <option value="preventiv">Preventiv</option>
                        </select>
                        <Toggle value={currency} onChange={setCurrency} options={[["EUR", "EUR"], ["LEK", "LEK"]]} testId="invoice-currency" />
                        <Toggle value={format} onChange={setFormat} options={[["a4", "A4"], ["80mm", "80mm"]]} testId="invoice-format" />
                    </div>
                    <button onClick={onClose} className="shrink-0" data-testid="invoice-modal-close"><X className="w-4 h-4" /></button>
                </div>

                <div className="px-4 py-2 border-b border-slate-200 flex flex-wrap items-center gap-2">
                    <button onClick={printA4} disabled={printing} className="btn-primary !py-1.5 !text-xs" data-testid="invoice-print-a4">
                        {printing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />} Print A4
                    </button>
                    <button onClick={print80} className="btn-primary !py-1.5 !text-xs" data-testid="invoice-print-80mm">
                        <Printer className="w-3.5 h-3.5" /> Print 80mm
                    </button>
                    <button
                        type="button"
                        onClick={() => downloadAuthed(`/admin/sales/${sale.id}/pdf`, `${docType}-${sale.id.slice(0, 8)}.pdf`, { type: docType, currency }).catch((e) => alert(e.message))}
                        className="btn-outline !py-1.5 !text-xs"
                        data-testid="invoice-download-pdf"
                    >
                        <FileText className="w-3.5 h-3.5" /> PDF A4
                    </button>
                    <button onClick={() => onEdit(sale)} className="btn-outline !py-1.5 !text-xs" data-testid="invoice-edit-sale">
                        <Pencil className="w-3.5 h-3.5" /> Modifiko
                    </button>
                    {sale.last_print && (
                        <span className="ml-auto font-mono text-[10px] text-slate-500" data-testid="invoice-last-print">
                            Printuar: {formatPrintLog(sale.last_print)}
                        </span>
                    )}
                </div>

                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50" data-testid="invoice-reasons-panel">
                    <FailureReasonsPicker value={sale.failure_reasons || []} onChange={(list) => saveExtras({ failure_reasons: list })} testIdPrefix="invoice-reason" />
                    <div className="mt-3">
                        <FailureReasonsPicker kind="services" value={sale.services || []} onChange={(list) => saveExtras({ services: list })} testIdPrefix="invoice-service" />
                    </div>
                    <div className="mt-2">
                        <ServiceNoteField value={serviceNote} onChange={(v) => { setServiceNote(v); setSale((s) => ({ ...s, service_note: v })); }} onBlur={saveServiceNote} testId="invoice-service-note" />
                    </div>
                </div>

                <div className="px-4 pt-3 flex items-center gap-2">
                    <Toggle value={view} onChange={setView} options={[["pdf", "Si printohet (PDF)"], ["html", "Pamje e thjeshtë"]]} testId="invoice-view" />
                </div>
                {view === "pdf" ? (
                    <div className="p-4 bg-slate-100 mt-3 flex justify-center" data-testid="invoice-pdf-view">
                        {pdfUrl ? (
                            <iframe title="invoice-pdf" src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`} className="bg-white border border-slate-300" style={{ width: format === "80mm" ? 320 : "100%", height: format === "80mm" ? 640 : 900 }} data-testid="invoice-pdf-iframe" />
                        ) : (
                            <div className="h-64 flex items-center justify-center font-mono text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Duke ngarkuar PDF…</div>
                        )}
                    </div>
                ) : (
                    <InvoicePreview sale={sale} content={content} docType={docType} currency={currency} />
                )}
            </div>

            <div onClick={(e) => e.stopPropagation()}>
                <ReceiptPrinter
                    open={receiptOpen}
                    sale={sale}
                    docType={docType}
                    currency={currency}
                    onClose={() => setReceiptOpen(false)}
                    onPrinted={(method) => logPrint(method, "80mm")}
                />
            </div>
        </div>
    );
}
